import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  updatePaymentRequestTemplate,
  deletePaymentRequestTemplate,
} from "@api";
import { Button, Input } from "@components";
import { toast } from "@components/toast/Toast";
import { usePaymentProfilesContext } from "@context/PaymentProfilesContext";
import { usePaymentRequestTemplatesContext } from "@context/PaymentRequestTemplatesContext";
import {
  SectionHeader,
  SectionTitle,
  SectionTagline,
  ControlStrip,
} from "../components";
import { PlusLg } from "react-bootstrap-icons";
import CreateEditTemplate from "../modals/CreateEditTemplate";
import SettingsMenuModal from "../modals/SettingsMenuModal";
import TemplateTile, {
  buildTags,
} from "@components/paymentTemplates/TemplateTile";
import { Search } from "react-bootstrap-icons";
import { useMerchant, canUpdatePaymentRequestTemplate } from "@auth";

function Templates() {
  const { hasPermission } = useMerchant();
  const canEdit = hasPermission(canUpdatePaymentRequestTemplate);
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [menuTemplate, setMenuTemplate] = useState(null);
  const { profiles: paymentProfiles } = usePaymentProfilesContext();
  const { templates, refresh: refreshTemplates } =
    usePaymentRequestTemplatesContext();

  useEffect(() => {
    if (location.state?.openCreate) {
      setEditTemplate(null);
      setModalOpen(true);
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  const openCreate = () => {
    setEditTemplate(null);
    setModalOpen(true);
  };

  const openEdit = (template) => {
    setEditTemplate(template);
    setModalOpen(true);
  };

  const openEditMenu = (template) => {
    setMenuTemplate(template);
  };

  const onModalComplete = () => {
    refreshTemplates();
    setModalOpen(false);
    setEditTemplate(null);
  };

  const onModalClose = () => {
    setModalOpen(false);
    setEditTemplate(null);
  };

  const onMenuComplete = () => {
    refreshTemplates();
    setMenuTemplate(null);
  };

  const handleRenameTemplate = async (template, newName) => {
    await updatePaymentRequestTemplate(
      template.id,
      newName,
      template.invoicesRequested,
      template.paymentRequirement,
      template.attachPdfStatement,
      template.messages,
      template.allowCardPayment,
      template.paymentProfileId,
    );
    toast({ description: `Renamed Template to "${newName}"` });
    onMenuComplete();
  };

  const handleDeleteTemplate = async (template) => {
    await deletePaymentRequestTemplate(template.id);
    toast({ description: `Deleted "${template.name}" Template` });
    onMenuComplete();
  };

  return (
    <div className="flex flex-col items-center py-4 px-5 border-b border-zinc-200">
      <div className="py-8 w-full max-w-4xl">
        <SectionHeader>
          <SectionTitle>Templates</SectionTitle>
          <SectionTagline>
            Create and manage the emails / texts sent to customers
          </SectionTagline>
        </SectionHeader>
        <ControlStrip>
          <div className="grow max-w-72">
            <Input
              placeholder="Search templates..."
              prefix={<Search />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          {canEdit && (
            <Button onClick={openCreate} color="primary">
              <PlusLg className="mr-2" />
              Create template
            </Button>
          )}
        </ControlStrip>
        <div className="flex flex-col gap-4 pt-6">
          {templates
            .filter((template) => {
              if (!searchQuery.trim()) return true;
              const q = searchQuery.toLowerCase();
              const nameMatch = template.name?.toLowerCase().includes(q);
              const tagMatch = buildTags(
                template,
                paymentProfiles,
              ).some((tag) => tag.toLowerCase().includes(q));
              return nameMatch || tagMatch;
            })
            .map((template) => (
              <TemplateTile
                key={template.id}
                template={template}
                onClick={openEdit}
                onMenuClick={canEdit ? openEditMenu : undefined}
                paymentProfiles={paymentProfiles}
              />
            ))}
        </div>
      </div>
      <CreateEditTemplate
        isOpen={modalOpen}
        onClose={onModalClose}
        template={editTemplate}
        onComplete={onModalComplete}
      />
      <SettingsMenuModal
        isOpen={!!menuTemplate}
        onClose={() => setMenuTemplate(null)}
        item={menuTemplate}
        itemLabel="template"
        onRename={handleRenameTemplate}
        onDelete={handleDeleteTemplate}
      />
    </div>
  );
}

export default Templates;
